/**
 * FAQ Category Management Page
 * 
 * Simple and fast category management interface.
 * Features:
 * - List view with FAQ count per category
 * - Modal-based add/edit for quick operations
 * - Disable (not delete) categories to preserve data integrity
 * - Warning when disabling category with published FAQs
 * 
 * UX Decisions:
 * - Modal editing keeps users in context
 * - Disable instead of delete prevents data loss
 * - FAQ count helps admins understand impact of changes
 * - Active/Inactive toggle with clear visual feedback
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ArrowBack as BackIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import MainCard from '../../../components/MainCard';
import CustomButton from '../../../components/@extended/CustomButton';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../../components/@extended/CustomTable';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import {
  useGetFaqCategoriesQuery,
  useCreateFaqCategoryMutation,
  useUpdateFaqCategoryMutation,
  useUpdateFaqCategoryStatusMutation,
} from '../../../store/slices/apiSlice';

// Validation schema for category form
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Category name is required')
    .max(255, 'Name must be 255 characters or less')
    .trim(),
  description: Yup.string()
    .max(1000, 'Description must be 1000 characters or less'),
  displayOrder: Yup.number()
    .min(0, 'Display order must be 0 or greater')
    .integer('Display order must be a whole number'),
});

// Category Form Modal
const CategoryFormModal = ({ open, onClose, category, onSave, isLoading }) => {
  const isEditMode = Boolean(category?.id);
  
  const formik = useFormik({
    initialValues: {
      name: category?.name || '',
      description: category?.description || '',
      displayOrder: category?.displayOrder || 0,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await onSave({
        name: values.name.trim(),
        description: values.description?.trim() || null,
        displayOrder: parseInt(values.displayOrder, 10) || 0,
        isActive: category?.isActive ?? true,
      });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {isEditMode ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            autoFocus
            id="name"
            name="name"
            label="Category Name"
            placeholder="e.g., Getting Started, Account & Billing"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            sx={{ mb: 3, mt: 1 }}
            required
          />
          
          <TextField
            fullWidth
            multiline
            rows={3}
            id="description"
            name="description"
            label="Description (Optional)"
            placeholder="Brief description of this category..."
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            sx={{ mb: 3 }}
          />
          
          <TextField
            fullWidth
            type="number"
            id="displayOrder"
            name="displayOrder"
            label="Display Order"
            value={formik.values.displayOrder}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.displayOrder && Boolean(formik.errors.displayOrder)}
            helperText={
              (formik.touched.displayOrder && formik.errors.displayOrder) ||
              'Lower numbers appear first'
            }
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <CustomButton 
            variant="outlined" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton 
            type="submit"
            variant="contained"
            disabled={isLoading || !formik.dirty || !formik.isValid}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isEditMode ? 'Save Changes' : 'Create Category'}
          </CustomButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Status toggle warning dialog
const StatusWarningDialog = ({ open, onClose, onConfirm, category, isLoading }) => {
  const publishedFaqCount = category?.faqs?.filter(f => f.isPublished !== false).length || 0;
  const isDisabling = category?.isActive;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        {isDisabling ? 'Disable Category' : 'Enable Category'}
      </DialogTitle>
      <DialogContent>
        {isDisabling && publishedFaqCount > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This category has <strong>{publishedFaqCount} published FAQ(s)</strong> that will be hidden from end users.
          </Alert>
        ) : null}
        <Typography>
          {isDisabling 
            ? `Are you sure you want to disable "${category?.name}"? FAQs in this category will no longer be visible to end users.`
            : `Are you sure you want to enable "${category?.name}"? Published FAQs in this category will become visible to end users.`
          }
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <CustomButton variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </CustomButton>
        <CustomButton 
          variant="contained"
          color={isDisabling ? 'warning' : 'success'}
          onClick={onConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isDisabling ? 'Disable' : 'Enable'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

// Loading skeleton
const TableSkeleton = () => (
  <TableBody>
    {[1, 2, 3, 4].map((row) => (
      <TableRow key={row}>
        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
        <TableCell><Skeleton variant="text" width="80%" /></TableCell>
        <TableCell><Skeleton variant="rounded" width={40} height={24} /></TableCell>
        <TableCell><Skeleton variant="rounded" width={50} height={24} /></TableCell>
        <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
      </TableRow>
    ))}
  </TableBody>
);

// Category card for mobile
const CategoryCard = ({ category, onEdit, onToggleStatus }) => {
  const faqCount = category.faqs?.length || 0;
  const publishedCount = category.faqs?.filter(f => f.isPublished !== false).length || 0;

  return (
    <Card 
      sx={{ 
        mb: 2,
        borderLeft: 4,
        borderLeftColor: category.isActive ? 'success.main' : 'grey.400',
        opacity: category.isActive ? 1 : 0.8,
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {category.name}
            </Typography>
            {category.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {category.description}
              </Typography>
            )}
          </Box>
          <Chip
            label={category.isActive ? 'Active' : 'Inactive'}
            size="small"
            color={category.isActive ? 'success' : 'default'}
            variant={category.isActive ? 'filled' : 'outlined'}
          />
        </Box>
        
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {faqCount} FAQ{faqCount !== 1 ? 's' : ''}
            {publishedCount > 0 && ` (${publishedCount} published)`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Order: {category.displayOrder}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Tooltip title={category.isActive ? 'Disable' : 'Enable'}>
          <Switch
            size="small"
            checked={category.isActive}
            onChange={() => onToggleStatus(category)}
            color="success"
          />
        </Tooltip>
        <IconButton size="small" onClick={() => onEdit(category)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
};

const CategoryManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [statusWarningOpen, setStatusWarningOpen] = useState(false);
  const [categoryToToggle, setCategoryToToggle] = useState(null);
  
  // API queries
  const { data: categoriesData, isLoading, error, refetch } = useGetFaqCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] = useCreateFaqCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateFaqCategoryMutation();
  const [updateCategoryStatus, { isLoading: isTogglingStatus }] = useUpdateFaqCategoryStatusMutation();

  const isSaving = isCreating || isUpdating;
  
  // Handlers
  const handleAddCategory = () => {
    setSelectedCategory(null);
    setFormModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedCategory(null);
  };

  const handleSaveCategory = async (values) => {
    try {
      if (selectedCategory?.id) {
        await updateCategory({ id: selectedCategory.id, payload: values }).unwrap();
      } else {
        await createCategory(values).unwrap();
      }
      handleCloseFormModal();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleToggleStatusClick = (category) => {
    setCategoryToToggle(category);
    setStatusWarningOpen(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (categoryToToggle) {
      try {
        await updateCategoryStatus({
          id: categoryToToggle.id,
          isActive: !categoryToToggle.isActive,
        }).unwrap();
        setStatusWarningOpen(false);
        setCategoryToToggle(null);
      } catch (error) {
        console.error('Failed to toggle category status:', error);
      }
    }
  };

  const handleCancelToggleStatus = () => {
    setStatusWarningOpen(false);
    setCategoryToToggle(null);
  };

  const categories = categoriesData || [];

  return (
    <MainCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CustomButton
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin/faqs')}
            sx={{ mr: 1 }}
          >
            Back
          </CustomButton>
          <Typography variant="h5">FAQ Categories</Typography>
        </Box>
      }
      secondary={
        <CustomButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCategory}
          size="small"
        >
          {isMobile ? 'Add' : 'Add Category'}
        </CustomButton>
      }
    >
      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load categories. Please try again later.
        </Alert>
      )}

      {/* Content */}
      {isLoading ? (
        isMobile ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <CustomTableHead>
                <TableRow>
                  <CustomTableHeadCell>Name</CustomTableHeadCell>
                  <CustomTableHeadCell>Description</CustomTableHeadCell>
                  <CustomTableHeadCell>FAQs</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                  <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
                </TableRow>
              </CustomTableHead>
              <TableSkeleton />
            </Table>
          </TableContainer>
        )
      ) : categories.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No categories yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first category to start organizing FAQs.
          </Typography>
          <CustomButton variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory}>
            Create First Category
          </CustomButton>
        </Box>
      ) : isMobile ? (
        // Mobile Card View
        <Box>
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={handleEditCategory}
              onToggleStatus={handleToggleStatusClick}
            />
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="FAQ categories table">
            <CustomTableHead>
              <TableRow>
                <CustomTableHeadCell>Name</CustomTableHeadCell>
                <CustomTableHeadCell>Description</CustomTableHeadCell>
                <CustomTableHeadCell align="center">FAQs</CustomTableHeadCell>
                <CustomTableHeadCell align="center">Status</CustomTableHeadCell>
                <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {categories.map((category) => {
                const faqCount = category.faqs?.length || 0;
                const publishedCount = category.faqs?.filter(f => f.isPublished !== false).length || 0;
                
                return (
                  <CustomTableRow 
                    key={category.id} 
                    hover
                    sx={{ opacity: category.isActive ? 1 : 0.7 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Order: {category.displayOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {category.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${publishedCount} published, ${faqCount - publishedCount} draft`}>
                        <Chip 
                          label={faqCount} 
                          size="small" 
                          variant="outlined"
                          color={faqCount > 0 ? 'primary' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={category.isActive ? 'Click to disable' : 'Click to enable'}>
                        <Switch
                          size="small"
                          checked={category.isActive}
                          onChange={() => handleToggleStatusClick(category)}
                          color="success"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditCategory(category)}
                          aria-label="Edit category"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </CustomTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Category Form Modal */}
      <CategoryFormModal
        open={formModalOpen}
        onClose={handleCloseFormModal}
        category={selectedCategory}
        onSave={handleSaveCategory}
        isLoading={isSaving}
      />

      {/* Status Toggle Warning Dialog */}
      <StatusWarningDialog
        open={statusWarningOpen}
        onClose={handleCancelToggleStatus}
        onConfirm={handleConfirmToggleStatus}
        category={categoryToToggle}
        isLoading={isTogglingStatus}
      />
    </MainCard>
  );
};

export default CategoryManagement;

